import { API_ROUTES } from 'routes/api';
import { StackedLine } from 'components/Charts/stacked-line';
import Select from 'react-select';
import { Doughnut } from 'components/Charts/doughnut';
import React, { useEffect, useState } from 'react';
import { ApiConsumer } from 'api/ApiConsumer';
import { getMonth, normalizePieChartData } from 'api/util';
import _isEmpty from 'lodash/isEmpty';
import { Chart } from 'react-google-charts';
import Loader from 'components/Loader';

const monthNames = [
  '', // Index 0 is empty since months start from 1
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const buildChannelReconsList = () => {
  const ret = Array(13); // 13 because we have 12 months and an extra row for the heading
  ret[0] = ['Plays', 'Recognised', 'Unrecognised'];
  for (let i = 1; i < ret.length; i++) {
    ret[i] = [getMonth(i), 0, 0];
  }
  return ret;
};



const getDynamicSeriesColors = (channelCount) => {
  // Base color palette (can be expanded as needed)
  const colorPalette = [
    '#4285F4', // Blue
    '#EA4335', // Red
    '#FBBC05', // Yellow
    '#34A853', // Green
    '#673AB7', // Purple
    '#FF9800', // Orange
    '#009688', // Teal
    '#9C27B0', // Deep Purple
    '#E91E63', // Pink
    '#00BCD4', // Cyan
    '#8BC34A', // Light Green
    '#FF5722', // Deep Orange
    '#607D8B', // Blue Grey
    '#795548', // Brown
    '#3F51B5', // Indigo
  ];

  // Create series object with dynamic colors
  const series = {};
  for (let i = 0; i < channelCount; i++) {
    series[i] = { color: colorPalette[i % colorPalette.length] };
  }
  return series;
};


const RecognitionLineChart = ({ graphData, loading: externalLoading }) => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataKey, setDataKey] = useState(0);

  useEffect(() => {
    // Wait for external loading to complete before processing
    if (externalLoading) {
      setLoading(true);
      return;
    }

    // Get current date
    const now = new Date();
    
    // Generate the last 12 months in order (oldest to newest)
    const monthsInOrder = [];
    for (let i = 11; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      monthsInOrder.push({
        monthNumber: date.getMonth() + 1,
        monthName: monthNames[date.getMonth() + 1]
      });
    }

    // Process channel data - handle empty case
    const channelNames = graphData && graphData.length > 0
      ? [...new Set(graphData.map(item => item.broadcast_stream__name).filter(Boolean))]
      : [];
    
    // Initialize data structure
    const chartDataMap = monthsInOrder.map(({ monthNumber, monthName }) => {
      const channelData = {};
      channelNames.forEach(channel => {
        channelData[channel] = 0;
      });
      return { monthNumber, monthName, channelData };
    });
    
    // Populate with actual data (if any)
    if (graphData && graphData.length > 0) {
      graphData.forEach(item => {
        if (!item.broadcast_stream__name) return;
        
        const month = item.month;
        const channel = item.broadcast_stream__name;
        const recognised = item.recognised;
        
        const monthEntry = chartDataMap.find(m => m.monthNumber === month);
        if (monthEntry && monthEntry.channelData[channel] !== undefined) {
          monthEntry.channelData[channel] = recognised;
        }
      });
    }
    
    // Convert to chart format
    // If no channels, still show months with empty data
    const data = channelNames.length > 0
      ? [
          ['Month', ...channelNames],
          ...chartDataMap.map(({ monthName, channelData }) => [
            monthName,
            ...channelNames.map(channel => channelData[channel])
          ])
        ]
      : [
          ['Month', 'No Data'],
          ...chartDataMap.map(({ monthName }) => [monthName, 0])
        ];
    
    setChartData(data);
    setDataKey(prev => prev + 1); // Force chart re-render when data changes
    setLoading(false);
  }, [graphData, externalLoading]);

  const options = {
    legend: { position: 'bottom' },
    chartArea: { width: '85%', height: '60%' },
    pointSize: 10,
    hAxis: {
      slantedText: true,
      slantedTextAngle: 45
    }
  };

  return (
    <div style={{ width: '100%', height: '500px' }}>
      {loading ? (
        <Loader upload_message={'loading'} full_height={true} />
      ) : chartData.length > 0 ? (
        <Chart
          key={dataKey}
          chartType="LineChart"
          width="100%"
          height="100%"
          data={chartData}
          options={options}
          legendToggle
        />
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <p>No data available</p>
        </div>
      )}
    </div>
  );
};

export function RecognitionSelector() {
  const [data, setData] = useState({});
  const [channelData, setChannelData] = useState({});
  const [loadingChannels, setLoadingChannels] = useState(true);
  const [selectedChannel, setSelectedChannel] = useState({
    value: 'ALL',
    label: 'ALL',
    name: 'ALL',
  });
  const [graphData, setGraphData] = useState([]);
  const [loadingChannelRecons, setLoadingChannelRecons] = useState(true);

  useEffect(() => {
    setLoadingChannels(true);
    ApiConsumer.get(API_ROUTES.BROADCAST.CHANNELS)
      .then((res) => {
        const newData = {};
        res.data?.forEach((channel) => {
          newData[channel.name] = buildChannelReconsList();
        });
        newData['ALL'] = buildChannelReconsList();
        setChannelData(res.data);
        setData(newData);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoadingChannels(false));
  }, []);

  useEffect(() => {
    if (!_isEmpty(channelData)) {
      setLoadingChannelRecons(true);
      ApiConsumer.get(API_ROUTES.BROADCAST.REPORTS.COMPARISON.STATION_MONTHLY)
        .then((res) => {
          const totals = buildChannelReconsList();
          const newData = { ...data };

          const RECOGNISED_COL = 1;
          const UNRECOGNISED_COL = 2;

          setGraphData(res.data);
          res?.data.forEach((item) => {
            if (!item.broadcast_stream__name) return;

            totals[item.month][RECOGNISED_COL] += item.recognised;
            totals[item.month][UNRECOGNISED_COL] += item.unrecognised;

            // build recons data by channel name
            if (!newData[item.broadcast_stream__name]) {
              newData[item.broadcast_stream__name] = buildChannelReconsList();
            } else {
              newData[item.broadcast_stream__name][item.month][
                RECOGNISED_COL
              ] += item.recognised;
              newData[item.broadcast_stream__name][item.month][
                UNRECOGNISED_COL
              ] += item.unrecognised;
            }

            newData['ALL'] = totals;
          });

          setData(newData);
        })
        .catch((err) => console.error(err))
        .finally(() => setLoadingChannelRecons(false));
    }
  }, [channelData]);

  const getOptions = () => {
    const ret = [{ value: 'ALL', label: 'ALL', name: 'ALL' }];
    for (let key in channelData) {
      ret.push({
        ...channelData[key],
        value: key,
        label: channelData[key]?.name,
      });
    }
    return ret;
  };

  return (
    <section className="grid grid-cols-1 xl:grid-cols-1 2xl:grid-cols-4 gap-4 mt-5">
      <div className="col-span-2">
        <h3 className="mb-2 font-medium profile-overview-title gap-2">
          Unrecognised vs Recognised (MTD)
        </h3>
        <Select
          isDisabled={loadingChannels}
          isLoading={loadingChannels}
          placeholder="All stations/channels"
          options={getOptions()}
          className={`text-xs w-full row-span-1 mb-3 border-gray-50`}
          defaultValue={getOptions()?.[0]}
          onChange={setSelectedChannel}
        />
        <Doughnut
          title="Unrecognised vs Recognised (MTD)"
          url={
            !selectedChannel.id
              ? API_ROUTES.BROADCAST.REPORTS.COMPARISON.ALL
              : API_ROUTES.BROADCAST.REPORTS.COMPARISON.ALL_BY_STATION(
                  selectedChannel.id,
                )
          }
          height="450px"
          normaliseData={normalizePieChartData}
          colors={["#f72585", "#5fa83f"]}
        />
      </div>
      <div className="col-span-2">
        <h3 className="mb-2 font-medium profile-overview-title gap-2">
          International vs National (MTD)
        </h3>
        <Doughnut
          title="International vs National (MTD)"
          url={
            !selectedChannel.id
              ? API_ROUTES.BROADCAST.REPORTS.COMPARISON.LOCATION
              : API_ROUTES.BROADCAST.REPORTS.COMPARISON.LOCATION_BY_STATION(
                  selectedChannel.id,
                )
          }
          height="500px"
          normaliseData={normalizePieChartData}
        />
      </div>
      <div className="col-span-4">
        <h3 className="mb-2 font-medium profile-overview-title">
          Recognition by Month (Total All Channels)
        </h3>
        <StackedLine
          existingData={data[selectedChannel.name] || []}
          hasExistingData
          loadingExistingData={loadingChannelRecons}
          title={'right'}
          height="500"
          width="80%"
        />
      </div>
      <div className="col-span-4">
        <h3 className="mb-2 font-medium profile-overview-title">
          Recognition by Channel (Month vs Month)
        </h3>
        <RecognitionLineChart graphData={graphData} loading={loadingChannelRecons} />
      </div>
    </section>
  );
}